// src/utils/VerifiedRoute.js
import { useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import FeedbackDialog from '../components/FeedbackDialog';

const VerifiedRoute = () => {
  const { user, emailVerified, profile, loading, authTokens } = useContext(AuthContext);
  const location = useLocation();
  const [feedbackRequest, setFeedbackRequest] = useState(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  // Check for active feedback requests when user is authenticated and verified
  useEffect(() => {
    const checkFeedbackRequest = async () => {
      if (!user || !emailVerified || !authTokens) {
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/';
        const response = await fetch(`${backendUrl}api/check-feedback-request/?platform=web`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authTokens.access}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.should_show) {
            setFeedbackRequest(data);
            setShowFeedbackDialog(true);
          }
        }
      } catch (error) {
        console.error('Failed to check feedback request:', error);
      }
    };

    checkFeedbackRequest();
  }, [user, emailVerified, authTokens]);

  // Show loading spinner while auth state is initializing
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If profile exists but emailVerified is explicitly false
  if (profile && emailVerified === false) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.username)}`} replace />;
  }

  // If we don't have profile data yet, wait for it
  if (!profile || !emailVerified) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Default case - allow access
  return (
    <>
      <Outlet />
      {feedbackRequest && (
        <FeedbackDialog
          open={showFeedbackDialog}
          onClose={() => setShowFeedbackDialog(false)}
          feedbackRequest={feedbackRequest}
          backendUrl={process.env.REACT_APP_API_URL || 'http://localhost:8000/'}
          authTokens={authTokens}
        />
      )}
    </>
  );
};

export default VerifiedRoute;